function normalizeMoneyValue(value) {
  if (value === null || value === undefined) {
    return null;
  }

  let normalized = value.toString().trim();
  if (normalized === '') {
    return null;
  }

  normalized = normalized.replace(/\s+/g, '');
  normalized = normalized.replace(/\$/g, '');
  normalized = normalized.replace(/[^0-9,.-]/g, '');

  if (normalized === '' || normalized === '-' || normalized === '.' || normalized === ',') {
    return null;
  }

  if (normalized.includes(',') && normalized.includes('.')) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (normalized.includes(',')) {
    normalized = normalized.replace(',', '.');
  }

  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

// Minimum confidence required before auto-selecting supplier/group from inferred matches.
const MIN_INFERENCE_CONFIDENCE = 0.40;
const RAPID_PART_DEBUG = true;

function normalizePartNumberForLookup(value) {
  return (value || '')
    .toString()
    .toUpperCase()
    .replace(/[\s-]+/g, '');
}

function normalizePartNumberForSave(value) {
  return (value || '')
    .toString()
    .replace(/\s+/g, '');
}

function buildPartLookupQueries(value) {
  const raw = (value || '').toString().trim();
  const normalized = normalizePartNumberForLookup(raw);

  if (!raw) {
    return [];
  }

  const queries = [raw];

  if (normalized && normalized !== raw) {
    queries.push(normalized);
  }

  // Build a LIKE-friendly pattern to match separators in DB part numbers.
  // Example: PXD1849D => P%X%D%1%8%4%9%D, so PX D1849-D is still returned.
  if (normalized.length > 1) {
    queries.push(normalized.split('').join('%'));
  }

  return Array.from(new Set(queries));
}

function normalizeMatchText(value) {
  const raw = (value || '').toString();
  const decomposed = typeof raw.normalize === 'function' ? raw.normalize('NFD') : raw;

  return decomposed
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeGroupMatchText(value) {
  return normalizeMatchText(stripLeadingIdLabel(value))
    .split(' ')
    .filter((token) => /^[a-z]+$/.test(token))
    .join(' ')
    .trim();
}

function normalizeSupplierMatchText(value) {
  return normalizeMatchText(stripLeadingIdLabel(value));
}

function getTokenSet(value) {
  const normalized = normalizeMatchText(value);
  if (!normalized) {
    return new Set();
  }
  return new Set(normalized.split(' ').filter((token) => token.length >= 2));
}

function getBigrams(value) {
  const compact = normalizeMatchText(value).replace(/\s+/g, '');
  if (!compact) {
    return [];
  }
  if (compact.length === 1) {
    return [compact];
  }
  const bigrams = [];
  for (let index = 0; index < compact.length - 1; index++) {
    bigrams.push(compact.slice(index, index + 2));
  }
  return bigrams;
}

function tokenOverlapScore(leftValue, rightValue) {
  const left = getTokenSet(leftValue);
  const right = getTokenSet(rightValue);

  if (left.size === 0 || right.size === 0) {
    return 0;
  }

  let intersection = 0;
  left.forEach((token) => {
    if (right.has(token)) {
      intersection += 1;
    }
  });

  return intersection / Math.max(left.size, right.size);
}

function bigramDiceScore(leftValue, rightValue) {
  const left = getBigrams(leftValue);
  const right = getBigrams(rightValue);

  if (left.length === 0 || right.length === 0) {
    return 0;
  }

  const leftCount = {};
  left.forEach((bigram) => {
    leftCount[bigram] = (leftCount[bigram] || 0) + 1;
  });

  let overlap = 0;
  right.forEach((bigram) => {
    if (leftCount[bigram] > 0) {
      overlap += 1;
      leftCount[bigram] -= 1;
    }
  });

  return (2 * overlap) / (left.length + right.length);
}

function scoreCandidateConfidence(candidateText, term, contextText) {
  const normalizedCandidate = normalizeMatchText(candidateText);
  const normalizedTerm = normalizeMatchText(term);
  const normalizedContext = normalizeMatchText(contextText);

  if (!normalizedCandidate) {
    return 0;
  }

  if (normalizedTerm && normalizedCandidate === normalizedTerm) {
    return 1;
  }

  const termTokenScore = tokenOverlapScore(normalizedTerm, normalizedCandidate);
  const termCharScore = bigramDiceScore(normalizedTerm, normalizedCandidate);
  const contextTokenScore = tokenOverlapScore(normalizedContext, normalizedCandidate);
  const contextCharScore = bigramDiceScore(normalizedContext, normalizedCandidate);

  let score = (Math.max(termTokenScore, termCharScore) * 0.7) + (Math.max(contextTokenScore, contextCharScore) * 0.3);

  if (normalizedTerm && (normalizedCandidate.includes(normalizedTerm) || normalizedTerm.includes(normalizedCandidate))) {
    score += 0.12;
  }

  if (normalizedContext && normalizedContext.includes(normalizedCandidate)) {
    score += 0.08;
  }

  return Math.max(0, Math.min(1, score));
}

function getDisplayName(option) {
  return option.text || option.name || option.label || '';
}

function hasValidRelationId(value) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0;
}

function splitKeywordList(value) {
  return (value || '')
    .toString()
    .split(/[;,|]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function getCandidateMatchCorpus(option) {
  if (!option) {
    return '';
  }

  const parts = [
    option.text,
    option.name,
    option.label,
    option.keywords,
    option.keyword,
    option.mots_clef,
    option.mot_clef,
    option.supplier_keywords,
    option.part_supplier_keywords,
    option.part_group_keywords,
    option.group_keywords,
  ];

  const merged = [];
  parts.forEach((value) => {
    if (!value) {
      return;
    }

    if (typeof value === 'string') {
      splitKeywordList(value).forEach((entry) => merged.push(entry));
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        splitKeywordList(entry).forEach((splitEntry) => merged.push(splitEntry));
      });
      return;
    }

    merged.push(value.toString());
  });

  return Array.from(new Set(merged)).join(' ');
}

function stripLeadingIdLabel(value) {
  const text = (value || '').toString().trim();
  if (!text) {
    return '';
  }

  // Common select2 labels like "123 - Supplier Name" or "(123) Supplier Name".
  let cleaned = text.replace(/^\s*[\[(]?\s*\d+\s*[\])]?\s*[-:|]?\s*/i, '');
  if (cleaned !== text && /[a-z]/i.test(cleaned)) {
    return cleaned.trim();
  }

  cleaned = text.replace(/^\s*\d+\s+(.+)$/i, '$1');
  return /[a-z]/i.test(cleaned) ? cleaned.trim() : text;
}

function formatMoneyValue(value, allowEmpty = true) {
  const parsed = normalizeMoneyValue(value);
  if (parsed === null) {
    return allowEmpty ? '' : '0.00';
  }
  return parsed.toFixed(2);
}

function getSellingFormula(cost, selling, percentProfit, minimumProfit) {
  let min;
  let pct;
  let result;

  cost = parseFloat(cost);
  selling = parseFloat(selling);

  if (!Number.isFinite(cost)) {
    cost = 0;
  }
  if (!Number.isFinite(selling)) {
    selling = 0;
  }

  if (percentProfit !== null && percentProfit !== undefined && percentProfit !== '') {
    pct = parseFloat(percentProfit) / 100;
    if (Number.isFinite(pct)) {
      result = cost + pct * cost;
    }
  }

  if (minimumProfit !== null && minimumProfit !== undefined && minimumProfit !== '') {
    min = parseFloat(minimumProfit);
    if (Number.isFinite(min)) {
      result = selling > cost + min ? selling : cost + min;
    }
  }

  if (min != null && pct != null && Number.isFinite(min) && Number.isFinite(pct)) {
    result = cost + pct * cost > cost + min ? cost + pct * cost : cost + min;
  } else if (min == null && pct == null) {
    result = selling;
  }

  if (!Number.isFinite(result)) {
    result = 0;
  }

  return result.toFixed(2);
}

function getMeaningfulLines(rawText) {
  return rawText
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/\u00a0/g, ' ').trim())
    .filter((line) => line !== '')
    .filter((line) => /[0-9A-Za-z]/.test(line));
}

function isPriceLine(line) {
  return /\$/.test(line) || /\d+[.,]\d{2}/.test(line);
}

function getNextMoneyAfter(lines, matcher) {
  const index = lines.findIndex((line) => matcher.test(line));
  if (index === -1) {
    return null;
  }

  for (let lineIndex = index + 1; lineIndex < lines.length; lineIndex++) {
    if (!isPriceLine(lines[lineIndex])) {
      continue;
    }
    const value = normalizeMoneyValue(lines[lineIndex]);
    if (value !== null) {
      return value;
    }
  }

  return null;
}

function getCoreAmount(lines) {
  const sourceLines = Array.isArray(lines) ? lines : [];

  for (let index = 0; index < sourceLines.length; index++) {
    const line = sourceLines[index] || '';
    if (!/\bcore\b/i.test(line)) {
      continue;
    }

    // First try to parse money on the same line, after the standalone word "core".
    const inlineCandidate = line.replace(/^.*\bcore\b[\s:\-]*/i, '').trim();
    const inlineValue = normalizeMoneyValue(inlineCandidate);
    if (inlineValue !== null) {
      return inlineValue;
    }

    // Then look at the next few lines (e.g. "Core price" followed by "$18.32").
    for (let offset = 1; offset <= 3; offset++) {
      const nextLine = sourceLines[index + offset];
      if (!nextLine) {
        break;
      }

      const parsed = normalizeMoneyValue(nextLine);
      if (parsed !== null) {
        return parsed;
      }

      if (!isPriceLine(nextLine) && /[:]/.test(nextLine)) {
        break;
      }
    }
  }

  return null;
}

function scoreCandidateName(line) {
  let score = line.length;

  if (/\d{3}\/\d{2}R\d{2}/i.test(line)) {
    score += 40;
  }
  if (/[A-Za-z].*[0-9]|[0-9].*[A-Za-z]/.test(line)) {
    score += 12;
  }
  if (/^[A-Z0-9\-\s]+$/.test(line)) {
    score -= 8;
  }
  if (line.length < 5) {
    score -= 20;
  }

  return score;
}

function pickBestName(lines, preferredIndexes = []) {
  const blockedPatterns = [
    /^part#/i,
    /^code produit/i,
    /^your price/i,
    /^sell price/i,
    /^buy from/i,
    /^order qty/i,
    /^cart$/i,
    /^buy now$/i,
    /^more information/i,
    /^buyers guide$/i,
    /^labor$/i,
    /^fet$/i,
    /^utqg:/i,
    /^load:/i,
    /^speed:/i,
    /^warranty:/i,
    /^sidewall:/i,
    /^years:/i,
    /^position:/i,
    /^includes /i,
    /^product detail:/i,
    /^product details:/i,
    /^primary\b/i,
    /^others\b/i,
    /^prochaine\b/i,
    /^\d+ jours\b/i,
    /^1 image$/i,
  ];

  for (const index of preferredIndexes) {
    if (index >= 0 && index < lines.length && !blockedPatterns.some((pattern) => pattern.test(lines[index]))) {
      return lines[index];
    }
  }

  const candidates = lines
    .map((line, index) => ({ line, index }))
    .filter((candidate) => !blockedPatterns.some((pattern) => pattern.test(candidate.line)));

  candidates.sort((left, right) => scoreCandidateName(right.line) - scoreCandidateName(left.line));
  return candidates[0] ? candidates[0].line : (lines[1] || lines[0] || '');
}

function extractLabeledValue(line) {
  if (!line) {
    return '';
  }

  const match = line.match(/^\s*[^:]+:\s*(.+)$/);
  if (!match) {
    return line.trim();
  }

  return match[1].trim();
}

function collectKeywordHints(text) {
  const lowerText = text.toLowerCase();
  const hints = [];

  const rules = [
    { pattern: /\b(pneu|tire|bsw|utqg|\d{3}\/\d{2}r\d{2})\b/i, terms: ['pneu', 'tire'] },
    { pattern: /\b(tpms)\b/i, terms: ['tpms'] },
    { pattern: /\b(hub cap|wheel cover|enjoliveur|cache moyeu)\b/i, terms: ['hub cap', 'wheel cover', 'enjoliveur'] },
    { pattern: /\b(spacer|wheel spacer|espaceur|élargisseur)\b/i, terms: ['spacer', 'espaceur', 'élargisseur'] },
    { pattern: /\b(plaquette|brake pad|pad set)\b/i, terms: ['plaquette', 'frein'] },
    { pattern: /\b(rotor|disque|disc brake)\b/i, terms: ['disque frein', 'frein'] },
    { pattern: /\b(filtre|filter)\b/i, terms: ['filtre', 'huile'] },
    { pattern: /\b(huile|oil)\b/i, terms: ['huile'] },
    { pattern: /\b(batterie|battery)\b/i, terms: ['batterie'] },
    { pattern: /\b(essuie|wiper)\b/i, terms: ['essuie glace'] },
    { pattern: /\b(amortisseur|shock|strut)\b/i, terms: ['amortisseur'] },
    { pattern: /\b(bearing|wheel hub|hub assembly|moyeu)\b/i, terms: ['roulement', 'moyeu'] },
  ];

  rules.forEach((rule) => {
    if (rule.pattern.test(lowerText)) {
      rule.terms.forEach((term) => hints.push(term));
    }
  });

  lowerText
    .split(/[^a-z0-9/]+/i)
    .filter((word) => word.length >= 5)
    .slice(0, 6)
    .forEach((word) => hints.push(word));

  return Array.from(new Set(hints));
}

function collectLineSearchHints(lines) {
  const hints = [];
  const sourceLines = (lines || []).slice(0, 30);

  sourceLines.forEach((line) => {
    const raw = (line || '').trim();
    if (!raw) {
      return;
    }

    hints.push(raw);

    const tokens = raw
      .replace(/[^A-Za-z0-9\s]/g, ' ')
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 2);

    if (tokens.length >= 2) {
      hints.push(`${tokens[0]} ${tokens[1]}`);
    }
    if (tokens.length >= 3) {
      hints.push(`${tokens[0]} ${tokens[1]} ${tokens[2]}`);
    }

    tokens.slice(0, 5).forEach((token) => hints.push(token));
  });

  return Array.from(new Set(hints));
}

function collectGroupLineSearchHints(lines) {
  return Array.from(new Set(
    (lines || [])
      .slice(0, 30)
      .map((line) => normalizeGroupMatchText(line))
      .filter((line) => line.length >= 2)
  ));
}

function computeTextSequenceScore(candidateText, sourceText, lines) {
  const candidate = normalizeMatchText(candidateText);
  if (!candidate) {
    return 0;
  }

  const normalizedSource = normalizeMatchText(sourceText || '');
  const normalizedLines = (lines || [])
    .map((line) => normalizeMatchText(line))
    .filter((line) => line.length > 0);

  let best = 0;

  // Exact line-level match should dominate ranking.
  if (normalizedLines.some((line) => line === candidate)) {
    return 1;
  }

  if (normalizedSource && normalizedSource.includes(candidate)) {
    best = Math.max(best, 0.96);
  }

  normalizedLines.forEach((line) => {
    if (!line) {
      return;
    }

    if (line.includes(candidate)) {
      best = Math.max(best, 0.95);
    }

    if (line.length >= 4 && candidate.includes(line)) {
      best = Math.max(best, 0.9);
    }

    best = Math.max(best, bigramDiceScore(line, candidate));
    best = Math.max(best, tokenOverlapScore(line, candidate));
  });

  if (normalizedSource) {
    best = Math.max(best, bigramDiceScore(normalizedSource, candidate) * 0.7);
  }

  return Math.max(0, Math.min(1, best));
}

function getSkuValue(lines, skuIndex) {
  if (skuIndex < 0) {
    return '';
  }

  const inline = (lines[skuIndex].match(/^SKU\s*:\s*(.+)$/i) || [])[1];
  return (inline || lines[skuIndex + 1] || '').trim();
}

function getMoneyValues(value) {
  const matches = (value || '').match(/\$\s*\d+(?:[\s.,]\d{2})|\d+[.,]\d{2}\s*\$/g) || [];
  return matches
    .map((match) => normalizeMoneyValue(match))
    .filter((amount) => amount !== null);
}

function parseDtTireTemplate(context) {
  const { lines, fullText } = context;
  const skuIndex = lines.findIndex((line) => /^SKU\s*:/i.test(line));
  const skuOffset = /^SKU\s*:\s*.+/i.test(lines[skuIndex] || '') ? 1 : 2;
  const specification = lines[skuIndex + skuOffset] || '';
  const productName = lines[skuIndex - 1] || '';
  const isWheel = /\b(Taille|Diam\. moyeu|Déport|offset|STEEL WHEEL)\b/i.test(fullText);

  return {
    template: isWheel ? 'dt-tire-wheel' : 'dt-tire-tire',
    template_label: isWheel ? 'DT Tire — Roue' : 'DT Tire — Pneu',
    part_number: getSkuValue(lines, skuIndex),
    part_name: [productName, specification].filter(Boolean).join(' - '),
    part_suggested_price: formatMoneyValue(getNextMoneyAfter(lines, /^PDSF$/i)),
    part_cost_price: formatMoneyValue(getNextMoneyAfter(lines, /^P$/i)),
    part_selling_price: formatMoneyValue(getNextMoneyAfter(lines, /^Prix de détail suggéré$/i)),
    supplierHints: ['DT tire'],
    groupHints: [isWheel ? 'RIM NEUF' : 'PNEUS'],
  };
}

function parseCanadaTireTemplate(context) {
  const { lines } = context;
  let prices = [];
  for (let index = lines.length - 1; index >= 0; index--) {
    prices = getMoneyValues(lines[index]);
    if (prices.length >= 2) {
      break;
    }
  }

  return {
    template: 'canada-tire',
    template_label: 'Canada Tire — Pneu',
    part_number: lines[1] || '',
    part_name: lines[0] || '',
    part_cost_price: prices[1] !== undefined ? formatMoneyValue(prices[1]) : '',
    part_selling_price: prices[0] !== undefined ? formatMoneyValue(prices[0]) : '',
    supplierHints: ['Canada Tire'],
    groupHints: ['PNEUS'],
  };
}

function parseDirectAutoImportTemplate(context) {
  const { lines, fullText } = context;
  const hasExplicitRimTerm = /\b(?:steel|alloy|aluminium|mag) wheel\b|\bjante\b|\brim\b/i.test(fullText);
  const hasRimSize = /\b\d{2,3}x\d(?:\.\d)?\b/i.test(fullText);
  const hasRimFitment = /\boffset\b|\bdéport\b|\bdiam(?:ètre|\.)? moyeu\b|\bentraxe\b|\bback\b/i.test(fullText);
  const isWheel = hasExplicitRimTerm || (hasRimSize && hasRimFitment);

  return {
    template: 'direct-auto-import',
    template_label: 'Direct Auto Import',
    part_number: lines[1] || '',
    part_name: lines.slice(2, 5).join(' - '),
    part_suggested_price: formatMoneyValue(getNextMoneyAfter(lines, /^PDSF$/i)),
    part_cost_price: '',
    part_selling_price: formatMoneyValue(getNextMoneyAfter(lines, /^Prix de vente$/i)),
    supplierHints: ['DAI', 'Direct Auto Import'],
    groupHints: isWheel ? ['RIM NEUF', 'jante'] : collectKeywordHints(fullText),
  };
}

const RAPID_PART_TEMPLATES = [
  {
    name: 'stox',
    matches: ({ fullText }) => /\bstox\b/i.test(fullText),
    parse: ({ lines }) => {
      const bodyLines = lines.filter((line) => !/\bstox\b/i.test(line));
      const prices = [];
      bodyLines.forEach((line) => getMoneyValues(line).forEach((amount) => prices.push(amount)));
      // Cost is written as P + cents without separator, e.g. P6838 => 68.38.
      const costLine = bodyLines.find((line) => /^P\d+$/i.test(line.trim()));
      const cost = costLine ? parseInt(costLine.trim().slice(1), 10) / 100 : null;

      return {
        template: 'stox',
        template_label: 'STOX',
        part_number: bodyLines[0] || '',
        part_name: bodyLines[1] || '',
        part_suggested_price: prices[1] !== undefined ? formatMoneyValue(prices[1]) : '',
        part_selling_price: prices[1] !== undefined ? formatMoneyValue(prices[1]) : '',
        part_cost_price: cost !== null ? cost.toFixed(2) : '',
        supplierHints: ['Stox'],
      };
    },
  },
  {
    name: 'dt-tire',
    matches: ({ lines, fullText }) => lines.some((line) => /^SKU\s*:/i.test(line)) && /^P$/im.test(fullText) && /Prix de détail suggéré/i.test(fullText),
    parse: parseDtTireTemplate,
  },
  {
    name: 'canada-tire',
    matches: ({ lines, fullText }) => lines.some((line) => getMoneyValues(line).length >= 2) && /RESERVE VISIBLE|Contactez le support pour plus de détail/i.test(fullText),
    parse: parseCanadaTireTemplate,
  },
  {
    name: 'direct-auto-import',
    matches: ({ fullText }) => /Prix de vente/i.test(fullText) && /\bPDSF\b/i.test(fullText) && /\b(?:Total|Offset|Hub|Back)\b/i.test(fullText),
    parse: parseDirectAutoImportTemplate,
  },
  {
    name: 'distributiontox',
    matches: ({ distributionIndex }) => distributionIndex !== -1,
    parse: ({ lines, distributionIndex }) => {
      const match = lines[distributionIndex].match(/code produit\s*([A-Z0-9\-]+)/i);
      const prices = lines
        .filter((line) => isPriceLine(line))
        .map((line) => normalizeMoneyValue(line))
        .filter((value) => value !== null);

      return {
        template: 'distributiontox',
        template_label: 'Distribution STOX',
        part_number: match ? match[1].trim() : '',
        part_name: extractLabeledValue(pickBestName(lines, [distributionIndex - 1, distributionIndex - 2])),
        part_suggested_price: prices[0] !== undefined ? prices[0].toFixed(2) : '',
        part_selling_price: prices[0] !== undefined ? prices[0].toFixed(2) : '',
        part_cost_price: prices[1] !== undefined ? prices[1].toFixed(2) : '',
      };
    },
  },
  {
    name: 'nexpart',
    matches: ({ partHashIndex }) => partHashIndex !== -1,
    parse: ({ lines, fullText, partHashIndex, fallbackPartNameLine }) => {
      const partLine = lines[partHashIndex];
      const match = partLine.match(/part#\s*([A-Z0-9\-]+)/i);
      const isTire = /\d{3}\/\d{2}R\d{2}/i.test(fullText) || /\bFET\b/i.test(fullText);

      const sellingPrice = isTire ? '' : formatMoneyValue(getNextMoneyAfter(lines, /^sell price$/i));
      return {
        template: isTire ? 'nextparttires' : 'nexpart',
        template_label: isTire ? 'Nexpart — Pneu' : 'Nexpart',
        part_number: match ? match[1].trim() : '',
        part_name: isTire
          ? extractLabeledValue(pickBestName(lines, [partHashIndex + 1, partHashIndex - 1, 1]))
          : (extractLabeledValue(lines[2] || '') || fallbackPartNameLine),
        part_suggested_price: sellingPrice,
        part_selling_price: sellingPrice,
        part_cost_price: formatMoneyValue(getNextMoneyAfter(lines, /^your price$/i)),
        part_tire_tax: isTire ? formatMoneyValue(getNextMoneyAfter(lines, /^fet$/i)) : '',
      };
    },
  },
];

function parseRawPartText(rawText) {
  console.log('[RapidPartModal] Analyse du texte brut pour extraire les informations sur la pièce...');
  const lines = getMeaningfulLines(rawText);

  if (lines.length === 0) {
    return {
      template: 'empty',
      part_number: '',
      part_name: '',
      part_suggested_price: '',
      part_cost_price: '',
      part_selling_price: '',
      part_tire_tax: '',
      part_core: '',
      keywordHints: [],
      supplierHints: [],
      groupHints: [],
    };
  }

  const fullText = lines.join('\n');
  const firstLines = lines.slice(0, 10);
  const context = {
    rawText,
    lines,
    fullText,
    partHashIndex: firstLines.findIndex((line) => /part#/i.test(line)),
    distributionIndex: firstLines.findIndex((line) => /code produit/i.test(line)),
    fallbackPartNameLine: lines[1] || lines[0] || '',
  };
  const selectedTemplate = RAPID_PART_TEMPLATES.find((template) => template.matches(context));
  const parsed = selectedTemplate
    ? selectedTemplate.parse(context)
    : {
        template: 'generic',
        template_label: 'Générique',
        part_number: lines[0] || '',
        part_name: context.fallbackPartNameLine,
      };

  parsed.part_name = parsed.part_name || context.fallbackPartNameLine;
  parsed.part_suggested_price = parsed.part_suggested_price || '';
  parsed.part_cost_price = parsed.part_cost_price || '';
  parsed.part_selling_price = parsed.part_selling_price || '';
  parsed.part_tire_tax = parsed.part_tire_tax || '';
  parsed.part_core = formatMoneyValue(getCoreAmount(lines));
  parsed.supplierHints = parsed.supplierHints || [];
  parsed.groupHints = parsed.groupHints || [];
  parsed.keywordHints = Array.from(new Set([
    ...collectKeywordHints(`${parsed.part_name}\n${fullText}`),
    ...parsed.groupHints,
  ]));

  console.log(`[RapidPartModal] Modèle ${parsed.template} reconnu avec ${lines.length} lignes utiles.`);
  return parsed;
}

function normalizePartForSelection(part) {
  const id = parseInt(part.part_id || part.id, 10);
  const partNumber = part.part_number || part.number || part.text || '';
  const partName = part.part_name || part.name || part.text || '';

  return {
    ...part,
    id: `part-${id}`,
    item_id: id,
    description: partName,
    scan: part.part_scan || part.scan || '',
    price: parseFloat(part.part_selling_price != null ? part.part_selling_price : (part.price != null ? part.price : part.part_suggested_price || 0)),
    tire_tax: parseFloat(part.part_tire_tax != null ? part.part_tire_tax : (part.tire_tax != null ? part.tire_tax : 0)),
    type: 'part',
    selected: true,
    text: partNumber,
    label: partNumber,
    name: partName,
    part_id: id,
    part_inventory: parseFloat(part.part_inventory || 0),
    part_return: parseFloat(part.part_return || 0),
  };
}

function buildPayload(popup) {
  const selectedGroup = popup.find('.rapid-part-group').data('selected-group') || null;
  const saveMode = getSelectedSaveMode(popup);
  const payload = {
    part_number: normalizePartNumberForSave(popup.find('.rapid-part-number').val().trim()),
    part_name: popup.find('.rapid-part-name').val().trim(),
    part_suggested_price: popup.find('.rapid-part-suggested-price').val().trim(),
    part_cost_price: popup.find('.rapid-part-cost-price').val().trim(),
    part_selling_price: popup.find('.rapid-part-selling-price').val().trim(),
    part_tire_tax: popup.find('.rapid-part-tire-tax').val().trim(),
    part_amount_in_inventory: '0',
  };

  if (saveMode === 'create') {
    payload.part_core = popup.find('.rapid-part-core').val().trim();
  }

  if (selectedGroup && selectedGroup.id) {
    payload.part_part_group_id = selectedGroup.id;
  }

  const selectedSupplier = popup.find('.rapid-part-supplier').data('selected-supplier') || null;
  if (selectedSupplier && selectedSupplier.id) {
    payload.part_supplier_id = selectedSupplier.id;
  } else {
    const supplierId = popup.data('default-supplier-id');
    if (supplierId) {
      payload.part_supplier_id = supplierId;
    }
  }

  return payload;
}

function setConfidenceState(popup, kind, confidence, accepted, label) {
  const target = kind === 'supplier'
    ? popup.find('.rapid-part-supplier-confidence')
    : popup.find('.rapid-part-group-confidence');

  if (!Number.isFinite(confidence)) {
    target.hide().empty();
    return;
  }

  const percent = Math.round(confidence * 100);
  const color = accepted ? '#2e7d32' : '#c62828';
  const icon = accepted ? 'fa-check-circle' : 'fa-times-circle';
  const title = label ? `${label} (${percent}%)` : `${percent}%`;

  target
    .show()
    .html(`<span title="${title}" style="color:${color};"><i class="fa ${icon}"></i> ${percent}%</span>`);
}

function updateSaveButtonState(popup) {
  const button = popup.find('.rapid-part-confirm');
  const partNumber = popup.find('.rapid-part-number').val().trim();
  const partName = popup.find('.rapid-part-name').val().trim();
  button.prop('disabled', partNumber === '' || partName === '');
}

function highlightChangedFields(popup, existingPart) {
  let hasChanges = false;

  function normalizeRelationId(value) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function applyHighlight(field, changed) {
    field.closest('.form-group').css('background-color', changed ? '#fffbe6' : '');
    field.css('border-color', changed ? '#f0ad4e' : '');

    const select2Selection = field.siblings('.select2-container').find('.select2-selection');
    if (select2Selection.length > 0) {
      select2Selection.css('border-color', changed ? '#f0ad4e' : '');
    }
  }

  function setPreviousHint(prevSelector, value, changed, fieldSelector, relationValue) {
    if (!prevSelector) {
      return;
    }

    const target = popup.find(prevSelector);
    if (!target.length) {
      return;
    }

    const previousState = target.data('rapid-part-toggle-state');
    if (!value || (!changed && (!previousState || !previousState.toggled))) {
      target.removeData('rapid-part-toggle-state');
      target.text('').removeAttr('title');
      return;
    }

    const field = popup.find(fieldSelector);
    const activeRelationValue = relationValue || (previousState && previousState.relationValue ? previousState : null);
    if (!field.is('input') && !(field.is('select') && activeRelationValue)) {
      target
        .removeData('rapid-part-toggle-state')
        .removeClass('text-warning text-success')
        .addClass('text-warning')
        .text(`Valeur précédente: ${value}`)
        .removeAttr('title');
      return;
    }

    let toggleState = target.data('rapid-part-toggle-state');
    if (!toggleState) {
      toggleState = {
        alternateValue: relationValue ? relationValue.value : value,
        alternateText: value,
        relationValue: !!relationValue,
        dataKey: relationValue ? relationValue.dataKey : '',
        toggled: false,
      };
      target.data('rapid-part-toggle-state', toggleState);
    }

    const label = toggleState.toggled ? 'Valeur entrée:' : 'Valeur précédente:';
    const colorClass = toggleState.toggled ? 'text-success' : 'text-warning';
    const tooltip = toggleState.toggled
      ? 'Revenir à la valeur source'
      : 'Utiliser valeur précédente';
    const toggleValue = $('<span class="rapid-part-toggle-value" role="button" tabindex="0" style="cursor:pointer;" />')
      .attr('data-field', fieldSelector)
      .attr('title', tooltip)
      .attr('aria-label', tooltip)
      .append($('<i class="fa fa-exchange" aria-hidden="true" style="font-size:10px; margin-right:3px;" />'))
      .append($('<span />').text(toggleState.alternateText));

    target
      .empty()
      .removeClass('text-warning text-success')
      .addClass(colorClass)
      .append($('<span />').text(`${label} `))
      .append(toggleValue)
      .append(' ')
      .attr('title', tooltip);
  }

  const fields = [
    { selector: '.rapid-part-name', existingVal: (existingPart.part_name || existingPart.name || '').trim(), isMoney: false },
    { selector: '.rapid-part-suggested-price', existingVal: formatMoneyValue(existingPart.part_suggested_price), isMoney: true },
    { selector: '.rapid-part-cost-price', existingVal: formatMoneyValue(existingPart.part_cost_price), isMoney: true },
    { selector: '.rapid-part-selling-price', existingVal: formatMoneyValue(existingPart.part_selling_price), isMoney: true },
    { selector: '.rapid-part-tire-tax', existingVal: formatMoneyValue(existingPart.part_tire_tax, false), isMoney: true },
    { selector: '.rapid-part-core', existingVal: formatMoneyValue(existingPart.part_core), isMoney: true },
    { selector: '.rapid-part-supplier', existingVal: normalizeRelationId(existingPart.part_supplier_id), isRelation: true, dataKey: 'selected-supplier' },
    { selector: '.rapid-part-group', existingVal: normalizeRelationId(existingPart.part_part_group_id), isRelation: true, dataKey: 'selected-group' },
  ];

  const previousHints = {
    '.rapid-part-name': { selector: '.rapid-part-prev-name', value: (existingPart.part_name || existingPart.name || '').trim() },
    '.rapid-part-suggested-price': { selector: '.rapid-part-prev-suggested-price', value: formatMoneyValue(existingPart.part_suggested_price) },
    '.rapid-part-cost-price': { selector: '.rapid-part-prev-cost-price', value: formatMoneyValue(existingPart.part_cost_price) },
    '.rapid-part-selling-price': { selector: '.rapid-part-prev-selling-price', value: formatMoneyValue(existingPart.part_selling_price) },
    '.rapid-part-tire-tax': { selector: '.rapid-part-prev-tire-tax', value: formatMoneyValue(existingPart.part_tire_tax, false) },
    '.rapid-part-core': { selector: '.rapid-part-prev-core', value: formatMoneyValue(existingPart.part_core) },
    '.rapid-part-supplier': { selector: '.rapid-part-prev-supplier', value: existingPart.supplier_name || (hasValidRelationId(existingPart.part_supplier_id) ? `#${existingPart.part_supplier_id}` : '') },
    '.rapid-part-group': { selector: '.rapid-part-prev-group', value: existingPart.part_group_name || (hasValidRelationId(existingPart.part_part_group_id) ? `#${existingPart.part_part_group_id}` : '') },
  };

  fields.forEach(({ selector, existingVal, isMoney, isRelation, dataKey }) => {
    const field = popup.find(selector);
    const currentValRaw = field.val();
    const currentVal = currentValRaw == null ? '' : currentValRaw.toString().trim();
    let changed = false;

    if (isRelation) {
      const selected = field.data(dataKey) || null;
      const selectedId = normalizeRelationId((selected && selected.id) || currentVal);
      changed = selectedId !== existingVal;
    } else if (isMoney) {
      const existingNum = normalizeMoneyValue(existingVal);
      const currentNum = normalizeMoneyValue(currentVal);

      if (currentVal === '') {
        changed = existingNum !== null;
      } else if (currentNum !== null) {
        if (existingNum === null) {
          changed = true;
        } else {
          changed = formatMoneyValue(currentVal) !== formatMoneyValue(existingVal);
        }
      } else {
        changed = true;
      }
    } else {
      changed = currentVal !== existingVal.trim();
    }

    if (changed) {
      hasChanges = true;
    }

    applyHighlight(field, changed);
    const hint = previousHints[selector] || null;
    let hintValue = hint ? hint.value : '';
    let toggleRelation = null;
    if (isRelation && changed) {
      const selected = field.data(dataKey) || null;
      const selectedName = selected ? getDisplayName(selected) : '';
      const existingName = selector === '.rapid-part-supplier'
        ? (existingPart.supplier_name || '')
        : (existingPart.part_group_name || '');
      if (
        selectedName && existingName &&
        normalizeMatchText(selectedName) === normalizeMatchText(existingName) &&
        normalizeRelationId(selected && selected.id) !== existingVal
      ) {
        hintValue = `${hintValue} (#${existingVal})`;
      }

      if (hasValidRelationId(existingVal)) {
        const previousRelation = selector === '.rapid-part-supplier'
          ? { id: existingVal, text: existingPart.supplier_name || hintValue }
          : {
              id: existingVal,
              text: existingPart.part_group_name || hintValue,
              percent_profit: existingPart.percent_profit,
              minimum_profit: existingPart.minimum_profit,
              part_group_tire_tax: existingPart.part_group_tire_tax,
            };
        toggleRelation = { value: previousRelation, dataKey };
      }
    }
    setPreviousHint(hint ? hint.selector : '', hintValue, changed, selector, toggleRelation);
  });

  return hasChanges;
}

function clearFieldHighlights(popup) {
  popup.find('.rapid-part-number, .rapid-part-name, .rapid-part-suggested-price, .rapid-part-cost-price, .rapid-part-selling-price, .rapid-part-tire-tax, .rapid-part-core, .rapid-part-supplier, .rapid-part-group').each(function() {
    $(this).css('border-color', '');
    $(this).closest('.form-group').css('background-color', '');
    const select2Selection = $(this).siblings('.select2-container').find('.select2-selection');
    if (select2Selection.length > 0) {
      select2Selection.css('border-color', '');
    }
  });

  popup.find('.rapid-part-prev')
    .text('')
    .removeData('rapid-part-toggle-state')
    .removeAttr('title')
    .removeClass('text-warning text-success');
}

function refreshExistingDiffState(popup) {
  const existingPart = popup.data('existing-part-ref');
  if (!existingPart) {
    return;
  }

  const hasChanges = highlightChangedFields(popup, existingPart);
  if (!popup.data('existing-part-other-supplier')) {
    setSaveModeState(popup, {
      visible: true,
      allowCreate: true,
      allowExisting: true,
      selectedMode: 'existing',
      existingLabel: 'Mettre à jour infos pièce',
    });
  }
}

function hasSelectedGroup(popup) {
  const selectedGroup = popup.find('.rapid-part-group').data('selected-group');
  return !!(selectedGroup && selectedGroup.id);
}

function setStatusMessage(popup, message, type) {
  const status = popup.find('.rapid-part-status');
  if (!message) {
    status
      .hide()
      .removeClass('alert-info alert-success alert-warning')
      .css({ 'background-color': '', 'border-color': '', color: '' })
      .empty();
    return;
  }

  if (type === 'alert-pink') {
    status
      .show()
      .removeClass('alert-info alert-success alert-warning')
      .css({ 'background-color': '#ffe6f2', 'border-color': '#f4b2cf', color: '#7a1847' })
      .html(message);
    return;
  }

  status
    .show()
    .removeClass('alert-info alert-success alert-warning')
    .css({ 'background-color': '', 'border-color': '', color: '' })
    .addClass(type || 'alert-info')
    .html(message);
}

function setSaveModeState(popup, config = {}) {
  const container = popup.find('.rapid-part-save-mode');
  const createRadio = popup.find('.rapid-part-save-mode-create');
  const existingRadio = popup.find('.rapid-part-save-mode-existing');
  const existingLabel = popup.find('.rapid-part-save-mode-existing-label');

  if (config.visible === false) {
    container.hide();
    createRadio.prop({ checked: false, disabled: true });
    existingRadio.prop({ checked: false, disabled: true });
    existingLabel.text('Mettre à jour infos pièce');
    popup.data('rapid-part-save-mode', 'create');
    popup.find('.rapid-part-core').prop('disabled', false);
    return;
  }

  container.show();

  const allowCreate = config.allowCreate !== false;
  const allowExisting = !!config.allowExisting;
  const selectedMode = config.selectedMode === 'existing' && allowExisting ? 'existing' : 'create';

  createRadio.prop({ checked: selectedMode === 'create', disabled: !allowCreate });
  existingRadio.prop({ checked: selectedMode === 'existing', disabled: !allowExisting });
  existingLabel.text(config.existingLabel || 'Mettre à jour infos pièce');
  popup.data('rapid-part-save-mode', selectedMode);

  popup.find('.rapid-part-core').prop('disabled', selectedMode !== 'create');
}

function getSelectedSaveMode(popup) {
  return popup.find('.rapid-part-save-mode-existing').is(':checked') ? 'existing' : 'create';
}

function setDebugMessage(popup, message) {
  if (!RAPID_PART_DEBUG) {
    return;
  }
  popup.find('.rapid-part-debug').text(message || '');
}

function applySupplierSelection(popup, supplier) {
  const select = popup.find('.rapid-part-supplier');

  if (!supplier || !supplier.id) {
    select.data('selected-supplier', null).val(null).trigger('change');
    popup.find('.rapid-part-supplier-summary').text('Aucun fournisseur détecté');
    setConfidenceState(popup, 'supplier');
    refreshExistingDiffState(popup);
    return;
  }

  const option = new Option(supplier.text || supplier.name, supplier.id, true, true);
  select.html('').append(option).trigger('change');
  select.data('selected-supplier', supplier);
  popup.find('.rapid-part-supplier-summary').text(supplier.text || supplier.name);
  refreshExistingDiffState(popup);
}

function applyGroupSelection(popup, group) {
  const select = popup.find('.rapid-part-group');

  if (!group || !group.id) {
    select.data('selected-group', null).val(null).trigger('change');
    popup.find('.rapid-part-group-summary').text('Aucun groupe détecté');
    popup.find('.rapid-part-group-profit').val('');
    popup.find('.rapid-part-group-minimum').val('');
    setConfidenceState(popup, 'group');
    refreshExistingDiffState(popup);
    return;
  }

  const option = new Option(group.text || group.name, group.id, true, true);
  select.html('').append(option).trigger('change');
  select.data('selected-group', group);
  popup.find('.rapid-part-group-summary').text(`${group.text || group.name} | % Profit: ${group.percent_profit || 0} | Profit min: ${group.minimum_profit || 0}`);
  popup.find('.rapid-part-group-profit').val(group.percent_profit == null ? '' : group.percent_profit);
  popup.find('.rapid-part-group-minimum').val(group.minimum_profit == null ? '' : group.minimum_profit);

  if (group.part_group_tire_tax != null && group.part_group_tire_tax !== '') {
    popup.find('.rapid-part-tire-tax').val(formatMoneyValue(group.part_group_tire_tax, false));
  }
  refreshExistingDiffState(popup);
}

function recomputeSellingPrice(popup) {
  if (!hasSelectedGroup(popup)) {
    return;
  }

  const cost = popup.find('.rapid-part-cost-price').val();
  const selling = popup.find('.rapid-part-selling-price').val();
  const percent = popup.find('.rapid-part-group-profit').val();
  const minimum = popup.find('.rapid-part-group-minimum').val();

  popup.find('.rapid-part-selling-price').val(getSellingFormula(cost, selling, percent, minimum));
  refreshExistingDiffState(popup);
}

function fetchJson(url, data) {
  return new Promise((resolve, reject) => {
    $.ajax({
      url,
      type: 'GET',
      data,
      dataType: 'json',
      success: resolve,
      error: reject,
    });
  });
}

async function fetchAllSelect2Options(baseEndpoint, endpointSuffix, maxPages = 25, seedSearches = []) {
  const optionsById = new Map();
  const searches = [''].concat(seedSearches || []);

  for (const search of searches) {
    for (let page = 1; page <= maxPages; page++) {
      const result = await fetchJson(`${baseEndpoint}${endpointSuffix}`, {
        search,
        page,
      });

      const rows = result.results || [];
      rows.forEach((item) => {
        const id = item && item.id;
        if (id !== undefined && id !== null && !optionsById.has(id)) {
          optionsById.set(id, item);
        }
      });

      const hasMore = !!(result.pagination && result.pagination.more);
      if (!hasMore || rows.length === 0) {
        break;
      }
    }

    // If empty search returned data, no need to seed-scan aggressively.
    if (search === '' && optionsById.size > 0) {
      break;
    }
  }

  return Array.from(optionsById.values());
}

const FOUND_PART_COLORS = ['#1e88e5', '#e53935', '#43a047', '#fb8c00', '#8e24aa', '#00acc1'];
const SCANNED_VALUE_COLOR = '#9e9e9e';

const FOUND_PART_FIELDS = [
  { selector: '.rapid-part-name', value: (part) => (part.part_name || part.name || '').trim() },
  { selector: '.rapid-part-suggested-price', value: (part) => formatMoneyValue(part.part_suggested_price) },
  { selector: '.rapid-part-cost-price', value: (part) => formatMoneyValue(part.part_cost_price) },
  { selector: '.rapid-part-selling-price', value: (part) => formatMoneyValue(part.part_selling_price) },
  { selector: '.rapid-part-tire-tax', value: (part) => formatMoneyValue(part.part_tire_tax, false) },
  { selector: '.rapid-part-core', value: (part) => formatMoneyValue(part.part_core) },
  { selector: '.rapid-part-group', value: (part) => part.part_group_name || '' },
];

function getFoundPartColor(index) {
  return FOUND_PART_COLORS[index % FOUND_PART_COLORS.length];
}

function buildColorDot(color) {
  return $('<span />').css({
    display: 'inline-block',
    width: '10px',
    height: '10px',
    'border-radius': '2px',
    'background-color': color,
    'margin-right': '5px',
  });
}

function renderFoundParts(popup, parts, selectedPart) {
  const list = popup.find('.rapid-part-found');
  list.empty();
  popup.find('.rapid-part-versions').remove();

  if (!parts || parts.length === 0) {
    list.hide();
    return;
  }

  parts.forEach((part, index) => {
    const isSelected = part === selectedPart;
    const modified = part.part_modified_datetime
      ? `Modifié le ${part.part_modified_datetime.toString().slice(0, 16)}${part.part_modified_user_name ? ` par ${part.part_modified_user_name}` : ''}`
      : 'Jamais modifié';

    $('<div class="rapid-part-found-item" role="button" tabindex="0" />')
      .attr('data-index', index)
      .css({ cursor: 'pointer', padding: '2px 4px', 'font-weight': isSelected ? 'bold' : 'normal', 'background-color': isSelected ? '#f5f5f5' : '' })
      .append(buildColorDot(getFoundPartColor(index)))
      .append($('<span />').text(part.supplier_name || 'Sans fournisseur'))
      .append($('<small class="text-muted" style="margin-left:6px;" />').text(modified))
      .appendTo(list);
  });
  list.show();

  const scanned = popup.data('scanned-values') || {};
  $('<div class="help-block" style="margin:2px 0 0 4px;" />')
    .append(buildColorDot(SCANNED_VALUE_COLOR))
    .append($('<span />').text('Texte collé (cliquer une valeur pour la réutiliser)'))
    .appendTo(list);

  FOUND_PART_FIELDS.forEach((field) => {
    const clickable = field.selector !== '.rapid-part-group';
    const container = $('<div class="rapid-part-versions help-block" style="margin-bottom:0;" />');
    const addRow = (value, color, bold, title) => {
      const row = $('<div />')
        .css({ 'font-weight': bold ? 'bold' : 'normal', padding: '2px 4px', 'background-color': bold ? '#f5f5f5' : '' })
        .append(buildColorDot(color))
        .append($('<span />').text(value === '' ? '—' : value));
      if (title) {
        row.attr('title', title);
      }
      if (clickable && value !== '') {
        row.addClass('rapid-part-version-value')
          .attr({ role: 'button', tabindex: 0, 'data-field': field.selector, 'data-value': value })
          .css('cursor', 'pointer');
      }
      row.appendTo(container);
    };

    parts.forEach((part, index) => addRow(field.value(part), getFoundPartColor(index), part === selectedPart, ''));
    if (clickable && scanned[field.selector]) {
      addRow(scanned[field.selector], SCANNED_VALUE_COLOR, false, 'Valeur du texte collé');
    }
    popup.find(field.selector).closest('.form-group').append(container);
  });
}

async function lookupExistingParts(baseEndpoint, partNumber, supplierId) {
  if (!partNumber) {
    return [];
  }

  const queries = buildPartLookupQueries(partNumber);
  const responses = await Promise.all(queries.map((search) => fetchJson(`${baseEndpoint}part/select2/part_number`, {
    search,
    page: 1,
    supplier_id: supplierId || -1,
  })));

  const candidatesById = new Map();
  responses.forEach((result) => {
    (result.results || []).forEach((item) => {
      const key = item.part_id || item.id || item.part_number || item.number;
      if (key !== undefined && key !== null && !candidatesById.has(key)) {
        candidatesById.set(key, item);
      }
    });
  });

  const candidates = Array.from(candidatesById.values());

  const normalizedPartNumber = normalizePartNumberForLookup(partNumber);
  return candidates.filter((item) => {
    const existingNumber = normalizePartNumberForLookup(item.part_number || item.number || item.text || '');
    return existingNumber === normalizedPartNumber;
  });
}

async function inferBestMatch(baseEndpoint, endpointSuffix, searchTerms, contextText, options = {}) {
  const ignoreLeadingIdInLabel = !!options.ignoreLeadingIdInLabel;
  const preloadedCandidates = Array.isArray(options.preloadedCandidates) ? options.preloadedCandidates : [];
  const sourceText = options.sourceText || '';
  const sourceLines = Array.isArray(options.lines) ? options.lines : [];
  let terms = Array.from(new Set(
    (searchTerms || [])
      .map((term) => normalizeMatchText(term))
      .filter((term) => term.length >= 2)
  )).slice(0, 20);

  if (terms.length === 0) {
    terms = Array.from(new Set(
      normalizeMatchText(contextText)
        .split(' ')
        .filter((term) => term.length >= 2)
    )).slice(0, 20);
  }

  if (terms.length === 0) {
    return { candidate: null, confidence: 0 };
  }

  const scoredById = new Map();

  if (preloadedCandidates.length > 0) {
    preloadedCandidates.forEach((item) => {
      if (typeof options.candidateFilter === 'function' && !options.candidateFilter(item)) {
        return;
      }
      const id = item.id;
      if (!id) {
        return;
      }

      const label = getDisplayName(item);
      const scoreLabel = ignoreLeadingIdInLabel ? stripLeadingIdLabel(label) : label;
      const scoreCorpus = getCandidateMatchCorpus(item) || scoreLabel;
      let confidence = 0;
      terms.forEach((term) => {
        confidence = Math.max(confidence, scoreCandidateConfidence(scoreCorpus, term, contextText));
      });
      confidence = Math.max(confidence, computeTextSequenceScore(scoreCorpus, sourceText, sourceLines));

      const current = scoredById.get(id);

      if (!current || confidence > current.confidence) {
        scoredById.set(id, {
          candidate: item,
          confidence,
        });
      }
    });
  } else {
    const responses = await Promise.all(
      terms.map((term) => fetchJson(`${baseEndpoint}${endpointSuffix}`, {
        search: term,
        page: 1,
      }).then((result) => ({ term, results: result.results || [] })))
    );

    responses.forEach(({ term, results }) => {
      results.forEach((item) => {
        if (typeof options.candidateFilter === 'function' && !options.candidateFilter(item)) {
          return;
        }
        const id = item.id;
        if (!id) {
          return;
        }

        const label = getDisplayName(item);
        const scoreLabel = ignoreLeadingIdInLabel ? stripLeadingIdLabel(label) : label;
        const scoreCorpus = getCandidateMatchCorpus(item) || scoreLabel;
        let confidence = scoreCandidateConfidence(scoreCorpus, term, contextText);
        confidence = Math.max(confidence, computeTextSequenceScore(scoreCorpus, sourceText, sourceLines));
        const current = scoredById.get(id);

        if (!current || confidence > current.confidence) {
          scoredById.set(id, {
            candidate: item,
            confidence,
          });
        }
      });
    });
  }

  let best = { candidate: null, confidence: 0 };
  scoredById.forEach((entry) => {
    if (entry.confidence > best.confidence) {
      best = entry;
    }
  });

  if (typeof options.onDebugBest === 'function') {
    options.onDebugBest(best);
  }

  return best;
}

async function inferExactNamedMatch(baseEndpoint, endpointSuffix, hints, options = {}) {
  const normalizeName = options.normalizeName || normalizeMatchText;
  const normalizedHints = Array.from(new Set(
    (hints || [])
      .map((hint) => normalizeName(hint))
      .filter((hint) => hint.length >= 2)
  ));
  const findExact = (candidates) => (candidates || []).find((candidate) => (
    normalizedHints.includes(normalizeName(getDisplayName(candidate)))
  ));

  let candidate = findExact(options.preloadedCandidates || []);
  if (!candidate) {
    const fetchCandidates = options.fetchCandidates || (async (hint) => {
      const result = await fetchJson(`${baseEndpoint}${endpointSuffix}`, {
        search: hint,
        page: 1,
      });
      return result.results || [];
    });

    for (const hint of normalizedHints) {
      candidate = findExact(await fetchCandidates(hint));
      if (candidate) {
        break;
      }
    }
  }

  const result = {
    candidate: candidate || null,
    confidence: candidate ? 1 : 0,
  };
  if (typeof options.onDebugBest === 'function') {
    options.onDebugBest(result);
  }
  return result;
}

function hasFixedTemplateGroup(parsed) {
  return parsed.template === 'dt-tire-wheel'
    || parsed.template === 'dt-tire-tire'
    || parsed.template === 'canada-tire';
}

function shouldInferGroupForPart() {
  return true;
}

async function inferGroup(baseEndpoint, parsed, options = {}) {
  const fixedGroupTemplate = hasFixedTemplateGroup(parsed);

  if (fixedGroupTemplate) {
    return inferExactNamedMatch(
      baseEndpoint,
      'part/select2/part_part_group_id',
      parsed.groupHints || [],
      {
        preloadedCandidates: options.preloadedCandidates || [],
        fetchCandidates: options.fetchCandidates,
        normalizeName: normalizeGroupMatchText,
        onDebugBest: options.onDebugBest,
      }
    );
  }

  const lineHints = collectGroupLineSearchHints(options.lines || []);
  const supplementalHints = [parsed.part_name, parsed.part_number, ...(parsed.keywordHints || []), ...lineHints];
  const searchTerms = [...(parsed.groupHints || []), ...supplementalHints]
    .filter((term) => term && term.trim() !== '')
    .map((term) => normalizeGroupMatchText(term))
    .filter((term) => term.length >= 2)
    .slice(0, 12);

  const contextText = searchTerms.join(' ');
  return inferBestMatch(baseEndpoint, 'part/select2/part_part_group_id', searchTerms, contextText, {
    preloadedCandidates: options.preloadedCandidates || [],
    sourceText: collectGroupLineSearchHints([options.sourceText || '']).join(' '),
    lines: lineHints,
    candidateFilter: (candidate) => normalizeGroupMatchText(getDisplayName(candidate)).length >= 2,
    onDebugBest: options.onDebugBest,
  });
}

function collectSupplierHints(parsed, lines) {
  const hints = [...(parsed.supplierHints || [])];

  // Template name is often the supplier
  if (parsed.template === 'nexpart' || parsed.template === 'nextparttires') {
    hints.push('nexpart');
  } else if (parsed.template === 'distributiontox') {
    hints.push('distribution');
  }

  // Scan first 5 meaningful lines for company-like tokens
  lines.slice(0, 5).forEach((line) => {
    if (
      line.length >= 4 &&
      line.length <= 60 &&
      !/^part#/i.test(line) &&
      !/^code produit/i.test(line) &&
      !(/^\d+([.,]\d{1,2})?$/.test(line))
    ) {
      hints.push(line.trim());
    }
  });

  return Array.from(new Set(hints));
}

async function inferSupplier(baseEndpoint, parsed, lines, options = {}) {
  const fixedDtTireSupplier = parsed.template === 'dt-tire-wheel' || parsed.template === 'dt-tire-tire';
  if (fixedDtTireSupplier) {
    return inferExactNamedMatch(
      baseEndpoint,
      'part/select2/part_supplier_id',
      ['DT tire'],
      {
        preloadedCandidates: options.preloadedCandidates || [],
        fetchCandidates: options.fetchCandidates,
        normalizeName: normalizeSupplierMatchText,
        onDebugBest: options.onDebugBest,
      }
    );
  }

  const lineHints = collectLineSearchHints(lines || []);
  const searchTerms = Array.from(new Set([
    ...collectSupplierHints(parsed, lines),
    ...lineHints,
    parsed.part_name,
    parsed.part_number,
  ].filter((term) => term && term.toString().trim() !== '')))
    .slice(0, 12);

  const contextText = [parsed.part_name, parsed.part_number, ...lineHints, ...searchTerms].join(' ');
  return inferBestMatch(baseEndpoint, 'part/select2/part_supplier_id', searchTerms, contextText, {
    ignoreLeadingIdInLabel: true,
    preloadedCandidates: options.preloadedCandidates || [],
    sourceText: options.sourceText || '',
    lines: lines || [],
    onDebugBest: options.onDebugBest,
  });
}

function renderRapidPartModal() {
  return `
    <div class="rapid-part-modal-body">
      <div class="row">
        <div class="col-md-6">
          <label class="control-label">Texte source <span class="label label-info rapid-part-template-badge" style="display:none; margin-left: 6px;"></span></label>
          <textarea class="form-control rapid-part-source" style="min-height: 200px; resize: vertical;" placeholder="Tapez, collez ou déposez le texte ici"></textarea>
          <div class="help-block">Fallback: ligne 1 = numéro de pièce, ligne 2 = nom.</div>
          <div class="form-group">
            <label class="control-label">Fournisseur <span class="rapid-part-supplier-confidence" style="display:none; margin-left: 6px;"></span></label>
            <select class="form-control rapid-part-supplier" style="width:100%"></select>
            <div class="help-block rapid-part-supplier-summary">Aucun fournisseur détecté</div>
            <div class="rapid-part-found" style="display:none; margin-bottom: 8px;"></div>
          </div>
          <div class="form-group">
            <label class="control-label">Groupe de pièces <span class="rapid-part-group-confidence" style="display:none; margin-left: 6px;"></span></label>
            <select class="form-control rapid-part-group" style="width:100%"></select>
            <div class="help-block rapid-part-group-summary">Aucun groupe détecté</div>
          </div>
          <input type="hidden" class="rapid-part-group-profit" />
          <input type="hidden" class="rapid-part-group-minimum" />
        </div>
        <div class="col-md-6">
          <div class="alert rapid-part-status" style="display:none; padding: 6px 10px; margin-bottom: 8px;"></div>
          <div class="help-block rapid-part-debug" style="min-height: 16px; margin-bottom: 8px;"></div>
          <div class="form-group" style="margin-bottom: 6px;">
            <label class="control-label">Modèle reconnu</label>
            <div class="rapid-part-template text-muted">Aucune analyse</div>
          </div>
          <div class="form-group">
            <label class="control-label">Numéro de pièce</label>
            <input type="text" class="form-control rapid-part-number" />
          </div>
          <div class="form-group">
            <label class="control-label">Nom</label>
            <input type="text" class="form-control rapid-part-name" />
          </div>
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="control-label">Prix suggéré</label>
                <input type="text" class="form-control rapid-part-suggested-price" />
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="control-label">Prix coûtant</label>
                <input type="text" class="form-control rapid-part-cost-price" />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="control-label">Prix vendant</label>
                <input type="text" class="form-control rapid-part-selling-price" />
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="control-label">T.V. Pneu</label>
                <input type="text" class="form-control rapid-part-tire-tax" />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="control-label">Consigne</label>
                <input type="text" class="form-control rapid-part-core" />
              </div>
            </div>
          </div>
          <div class="checkbox rapid-part-update-prices" style="display:none; margin-top: 0;">
            <div class="rapid-part-save-mode" style="display:none;">
              <label style="margin-right: 18px;">
                <input type="radio" name="rapid_part_save_mode" class="rapid-part-save-mode-create" value="create" /> Ajouter nouvelle pièce
              </label>
              <label>
                <input type="radio" name="rapid_part_save_mode" class="rapid-part-save-mode-existing" value="existing" /> <span class="rapid-part-save-mode-existing-label">Mettre à jour infos pièce</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>`;
}

function bindFieldEvents(popup) {
  const toggleValue = function(event) {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event.type === 'keydown') {
      event.preventDefault();
    }

    const fieldSelector = $(this).attr('data-field');
    const field = popup.find(fieldSelector);
    const hint = $(this).closest('.rapid-part-prev');
    const toggleState = hint.data('rapid-part-toggle-state');

    if (!field.length || !toggleState) {
      return;
    }

    if (toggleState.relationValue) {
      const dataKey = toggleState.dataKey;
      const currentRelation = field.data(dataKey) || {
        id: field.val(),
        text: field.find('option:selected').text(),
      };
      const selectedRelation = toggleState.alternateValue;
      toggleState.alternateValue = currentRelation;
      const currentName = getDisplayName(currentRelation);
      const selectedName = getDisplayName(selectedRelation);
      const existingPart = popup.data('existing-part-ref') || {};
      const existingName = dataKey === 'selected-supplier'
        ? (existingPart.supplier_name || '')
        : (existingPart.part_group_name || '');
      const currentId = parseInt(currentRelation.id, 10);
      const existingId = parseInt(dataKey === 'selected-supplier'
        ? existingPart.part_supplier_id
        : existingPart.part_part_group_id, 10);
      toggleState.alternateText = currentName && existingName &&
        normalizeMatchText(currentName) === normalizeMatchText(existingName) &&
        Number.isFinite(currentId) && Number.isFinite(existingId) && currentId !== existingId
        ? `${currentName} (#${currentId})`
        : currentName;
      toggleState.toggled = !toggleState.toggled;
      hint.data('rapid-part-toggle-state', toggleState);

      if (dataKey === 'selected-supplier') {
        applySupplierSelection(popup, selectedRelation);
        const refreshSupplierMismatch = popup.data('rapid-part-refresh-supplier-mismatch');
        if (typeof refreshSupplierMismatch === 'function') {
          refreshSupplierMismatch();
        }
      } else {
        applyGroupSelection(popup, selectedRelation);
      }
      return;
    }

    const currentValue = field.val() == null ? '' : field.val().toString();
    field.val(toggleState.alternateValue);
    toggleState.alternateValue = currentValue;
    toggleState.alternateText = currentValue;
    toggleState.toggled = !toggleState.toggled;
    hint.data('rapid-part-toggle-state', toggleState);
    field.trigger('input');
  };

  popup
    .off('.rapidPartToggleValue', '.rapid-part-toggle-value')
    .on('click.rapidPartToggleValue keydown.rapidPartToggleValue', '.rapid-part-toggle-value', toggleValue);

  popup.find('.rapid-part-suggested-price').on('input', function() {
    popup.find('.rapid-part-selling-price').val($(this).val());
    if (hasSelectedGroup(popup)) {
      recomputeSellingPrice(popup);
    }
    refreshExistingDiffState(popup);
  });

  popup.find('.rapid-part-cost-price').on('input', function() {
    recomputeSellingPrice(popup);
  });

  popup.find('.rapid-part-number, .rapid-part-name, .rapid-part-selling-price, .rapid-part-tire-tax').on('input', function() {
    updateSaveButtonState(popup);
    refreshExistingDiffState(popup);
  });

  popup.find('.rapid-part-core').on('input', function() {
    refreshExistingDiffState(popup);
  });

  popup.find('.rapid-part-save-mode-create, .rapid-part-save-mode-existing').on('change', function() {
    popup.data('rapid-part-save-mode', getSelectedSaveMode(popup));
    popup.find('.rapid-part-core').prop('disabled', getSelectedSaveMode(popup) !== 'create');
  });
}

function hydrateFromExistingPart(popup, existingPart) {
  const dbDefaults = {
    '.rapid-part-name': existingPart.part_name || existingPart.name || '',
    '.rapid-part-suggested-price': existingPart.part_suggested_price != null ? formatMoneyValue(existingPart.part_suggested_price) : '',
    '.rapid-part-cost-price': existingPart.part_cost_price != null ? formatMoneyValue(existingPart.part_cost_price) : '',
    '.rapid-part-selling-price': existingPart.part_selling_price != null ? formatMoneyValue(existingPart.part_selling_price) : '',
    '.rapid-part-tire-tax': existingPart.part_tire_tax != null ? formatMoneyValue(existingPart.part_tire_tax, false) : '',
    '.rapid-part-core': existingPart.part_core != null ? formatMoneyValue(existingPart.part_core) : '',
  };

  Object.keys(dbDefaults).forEach((selector) => {
    const input = popup.find(selector);
    const currentVal = input.val().trim();

    // Keep parsed/user-entered value when present, otherwise fallback to DB.
    if (currentVal === '') {
      input.val(dbDefaults[selector]);
    }
  });
}

function setParsedValues(popup, parsed) {
  popup.find('.rapid-part-template').text(parsed.template === 'empty' ? 'Aucune analyse' : (parsed.template_label || parsed.template));
  const templateBadge = popup.find('.rapid-part-template-badge');
  if (parsed.template === 'empty') {
    templateBadge.hide().empty();
  } else {
    templateBadge.text(`Modèle détecté : ${parsed.template_label || parsed.template}`).show();
  }
  popup.find('.rapid-part-number').val(parsed.part_number || '');
  popup.find('.rapid-part-name').val(parsed.part_name || '');
  popup.find('.rapid-part-suggested-price').val(parsed.part_suggested_price || '');
  popup.find('.rapid-part-cost-price').val(parsed.part_cost_price || '');
  popup.find('.rapid-part-selling-price').val(parsed.part_selling_price || '');
  popup.find('.rapid-part-tire-tax').val(parsed.part_tire_tax || '');
  popup.find('.rapid-part-core').val(parsed.part_core || '');
  updateSaveButtonState(popup);
}

function openRapidPartModal(options) {
  const popup = bootbox.dialog({
    title: options.title || 'Création rapide d\'une pièce',
    message: renderRapidPartModal(),
    className: 'modal-rapid-part',
    size: 'large',
    buttons: {
      confirm: {
        label: 'Appliquer',
        className: 'btn blue rapid-part-confirm',
        callback: function() {
          return false;
        }
      },
      cancel: {
        label: 'Annuler',
        className: 'btn',
      }
    }
  });

  popup.data('default-supplier-id', options.defaultSupplierId || '');

  function resolveDropdownParent() {
    const modal = popup.closest('.bootbox.modal, .modal');
    if (modal.length > 0) {
      return modal;
    }
    return popup;
  }

  function bringOpenSelect2AboveModal(dropdownParent) {
    const parent = dropdownParent && dropdownParent.length ? dropdownParent : resolveDropdownParent();
    const modalZIndex = parseInt(parent.css('z-index'), 10);
    const targetZIndex = Number.isFinite(modalZIndex) ? modalZIndex + 5 : 10650;

    $('.select2-container--open').css('z-index', targetZIndex);
  }

  let lookupSequence = 0;
  let inferSequence = 0;
  let existingPart = null;
  let foundParts = [];
  let supplierSelectionLocked = false;
  let groupSelectionLocked = false;
  let preloadedSuppliers = [];
  let preloadedGroups = [];
  let preloadOptionsPromise = null;
  let debugLastBestSupplier = null;
  let debugLastBestGroup = null;

  function refreshSupplierMismatchSuggestion() {
    if (!existingPart) {
      return;
    }

    const selectedSupplier = popup.find('.rapid-part-supplier').data('selected-supplier') || null;
    const existingSupplierId = hasValidRelationId(existingPart.part_supplier_id) ? parseInt(existingPart.part_supplier_id, 10) : null;
    const selectedSupplierId = hasValidRelationId(selectedSupplier && selectedSupplier.id) ? parseInt(selectedSupplier.id, 10) : null;

    if (existingSupplierId === null || selectedSupplierId === null) {
      return;
    }

    const wasOtherSupplier = !!popup.data('existing-part-other-supplier');
    const isOtherSupplier = existingSupplierId !== selectedSupplierId;
    popup.data('existing-part-other-supplier', isOtherSupplier);

    if (isOtherSupplier) {
      setStatusMessage(
        popup,
        `Pièce trouvée (autre fournisseur): <strong>${existingPart.part_number || existingPart.number}</strong> - ${existingPart.part_name || existingPart.name}`,
        'alert-warning'
      );

      if (!wasOtherSupplier) {
        setSaveModeState(popup, {
          visible: true,
          allowCreate: true,
          allowExisting: true,
          selectedMode: 'create',
          existingLabel: 'Utiliser pièce trouvée (autre fournisseur)',
        });
      }
      return;
    }

    setStatusMessage(
      popup,
      `Pièce trouvée: <strong>${existingPart.part_number || existingPart.number}</strong> - ${existingPart.part_name || existingPart.name}`,
      'alert-info'
    );

    if (wasOtherSupplier) {
      setSaveModeState(popup, {
        visible: true,
        allowCreate: true,
        allowExisting: true,
        selectedMode: 'existing',
        existingLabel: 'Mettre à jour infos pièce',
      });
    }
  }

  popup.data('rapid-part-refresh-supplier-mismatch', refreshSupplierMismatchSuggestion);

  function setExistingState(part) {
    existingPart = part;
    const updatePricesContainer = popup.find('.rapid-part-update-prices');
    let isOtherSupplierMatch = false;

    if (!part) {
      updatePricesContainer.show();
      setSaveModeState(popup, {
        visible: true,
        allowCreate: true,
        allowExisting: false,
        selectedMode: 'create',
        existingLabel: 'Mettre à jour infos pièce',
      });
      popup.find('.rapid-part-number').prop('readonly', false);
      clearFieldHighlights(popup);
      setStatusMessage(popup, '', 'alert-info');
      popup.data('existing-part-ref', null);
      popup.data('existing-part-other-supplier', false);
      setConfidenceState(popup, 'group');
      setConfidenceState(popup, 'supplier');
      return;
    }

    const defaultSupplierId = hasValidRelationId(options.defaultSupplierId) ? parseInt(options.defaultSupplierId, 10) : null;
    const matchedSupplierId = hasValidRelationId(part.part_supplier_id) ? parseInt(part.part_supplier_id, 10) : null;
    isOtherSupplierMatch = defaultSupplierId !== null && matchedSupplierId !== null && defaultSupplierId !== matchedSupplierId;

    hydrateFromExistingPart(popup, part);

    // Keep the canonical DB number, but preserve a parsed name when the source text provided one.
    popup.find('.rapid-part-number').val(part.part_number || part.number || '');
    if (!popup.find('.rapid-part-name').val().trim()) {
      popup.find('.rapid-part-name').val(part.part_name || part.name || '');
    }

    popup.find('.rapid-part-number').prop('readonly', true);
    setStatusMessage(
      popup,
      `${isOtherSupplierMatch ? 'Pièce trouvée (autre fournisseur)' : 'Pièce trouvée'}: <strong>${part.part_number || part.number}</strong> - ${part.part_name || part.name}`,
      isOtherSupplierMatch ? 'alert-warning' : 'alert-info'
    );
    updateSaveButtonState(popup);
    popup.data('existing-part-ref', part);
    popup.data('existing-part-other-supplier', isOtherSupplierMatch);
    updatePricesContainer.show();

    if (isOtherSupplierMatch) {
      setSaveModeState(popup, {
        visible: true,
        allowCreate: true,
        allowExisting: true,
        selectedMode: 'create',
        existingLabel: 'Utiliser pièce trouvée (autre fournisseur)',
      });
    } else {
      setSaveModeState(popup, {
        visible: true,
        allowCreate: true,
        allowExisting: true,
        selectedMode: 'existing',
        existingLabel: 'Mettre à jour infos pièce',
      });
    }

    setConfidenceState(popup, 'group');
    setConfidenceState(popup, 'supplier');
  }

  function selectFoundPart(part) {
    setExistingState(part);

    if (hasValidRelationId(part.part_supplier_id) && part.supplier_name) {
      applySupplierSelection(popup, { id: part.part_supplier_id, text: part.supplier_name });
    } else {
      applySupplierSelection(popup, null);
    }

    if (hasValidRelationId(part.part_part_group_id) && part.part_group_name) {
      applyGroupSelection(popup, {
        id: part.part_part_group_id,
        text: part.part_group_name,
        percent_profit: part.percent_profit,
        minimum_profit: part.minimum_profit,
        part_group_tire_tax: part.part_group_tire_tax,
      });
    } else {
      applyGroupSelection(popup, null);
    }

    // Set after the group so its tire tax default does not override the stored value.
    FOUND_PART_FIELDS.filter((field) => field.selector !== '.rapid-part-group').forEach((field) => {
      popup.find(field.selector).val(field.value(part));
    });

    supplierSelectionLocked = true;
    groupSelectionLocked = true;
    refreshSupplierMismatchSuggestion();
    highlightChangedFields(popup, part);
    updateSaveButtonState(popup);
    renderFoundParts(popup, foundParts, part);
  }

  // Switch the update target without touching the scanned input values.
  function switchExistingPart(part) {
    existingPart = part;
    popup.data('existing-part-ref', part);
    popup.find('.rapid-part-number').val(part.part_number || part.number || '');

    const selected = popup.find('.rapid-part-supplier').data('selected-supplier');
    const isOther = !(selected && String(selected.id) === String(part.part_supplier_id));
    popup.data('existing-part-other-supplier', isOther);
    setStatusMessage(
      popup,
      `${isOther ? 'Pièce trouvée (autre fournisseur)' : 'Pièce trouvée'}: <strong>${part.part_number || part.number}</strong> - ${part.part_name || part.name}`,
      isOther ? 'alert-warning' : 'alert-info'
    );
    setSaveModeState(popup, {
      visible: true,
      allowCreate: true,
      allowExisting: true,
      selectedMode: isOther ? 'create' : 'existing',
      existingLabel: isOther ? 'Utiliser pièce trouvée (autre fournisseur)' : 'Mettre à jour infos pièce',
    });
    highlightChangedFields(popup, part);
    renderFoundParts(popup, foundParts, part);
  }

  function syncExistingPartToSelectedSupplier() {
    const selected = popup.find('.rapid-part-supplier').data('selected-supplier');
    if (!selected || !selected.id || foundParts.length === 0) {
      return;
    }

    const part = foundParts.find((item) => String(item.part_supplier_id) === String(selected.id));
    if (part && part !== existingPart) {
      switchExistingPart(part);
    }
  }

  let supplierInferSequence = 0;

  async function analyzeSourceText() {
    supplierSelectionLocked = false;
    groupSelectionLocked = false;

    const rawText = popup.find('.rapid-part-source').val();
    const parsed = parseRawPartText(rawText);
    const meaningfulLines = rawText
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((line) => line.replace(/\u00a0/g, ' ').trim())
      .filter((line) => line !== '');
    setParsedValues(popup, parsed);
    popup.data('scanned-values', {
      '.rapid-part-name': parsed.part_name || '',
      '.rapid-part-suggested-price': parsed.part_suggested_price || '',
      '.rapid-part-cost-price': parsed.part_cost_price || '',
      '.rapid-part-selling-price': parsed.part_selling_price || '',
      '.rapid-part-tire-tax': parsed.part_tire_tax || '',
      '.rapid-part-core': parsed.part_core || '',
    });

    const currentLookup = ++lookupSequence;
    setExistingState(null);
    foundParts = [];
    renderFoundParts(popup, foundParts, null);

    if (parsed.part_number) {
      try {
        const matches = await lookupExistingParts(options.baseEndpoint, parsed.part_number, options.defaultSupplierId);
        if (currentLookup !== lookupSequence) {
          return;
        }

        const match = matches.find((item) => String(item.part_supplier_id) === String(options.defaultSupplierId)) || matches[0] || null;
        foundParts = matches;
        setExistingState(match);
        renderFoundParts(popup, foundParts, match);
        if (!match) {
          setStatusMessage(
            popup,
            `Nouvelle pièce: <strong>${parsed.part_number}</strong>`,
            'alert-pink'
          );
        }
        if (match && hasValidRelationId(match.part_part_group_id)) {
          if (match.part_group_name) {
            applyGroupSelection(popup, {
              id: match.part_part_group_id,
              text: match.part_group_name,
              percent_profit: match.percent_profit,
              minimum_profit: match.minimum_profit,
              part_group_tire_tax: match.part_group_tire_tax,
            });
            recomputeSellingPrice(popup);
          } else {
            applyGroupSelection(popup, null);
          }
        }
        if (match && hasValidRelationId(match.part_supplier_id)) {
          if (match.supplier_name) {
            applySupplierSelection(popup, {
              id: match.part_supplier_id,
              text: match.supplier_name,
            });
            refreshSupplierMismatchSuggestion();
          } else {
            applySupplierSelection(popup, null);
          }
        }
        if (match) {
          const hasChanges = highlightChangedFields(popup, match);
          if (!popup.data('existing-part-other-supplier')) {
            setSaveModeState(popup, {
              visible: true,
              allowCreate: true,
              allowExisting: true,
              selectedMode: 'existing',
              existingLabel: 'Mettre à jour infos pièce',
            });
          }
        }
      } catch (error) {
        if (currentLookup === lookupSequence) {
          setStatusMessage(popup, 'Impossible de vérifier la pièce existante pour le moment.', 'alert-info');
        }
      }
    }

    const shouldInferSupplier = true;
    const shouldInferGroup = shouldInferGroupForPart(parsed, existingPart);

    if ((shouldInferSupplier || shouldInferGroup) && (parsed.part_name || parsed.part_number || meaningfulLines.length > 0)) {
      const currentInfer = ++inferSequence;
      const currentSupplierInfer = ++supplierInferSequence;
      try {
        if (preloadOptionsPromise) {
          try {
            await preloadOptionsPromise;
          } catch (error) {
            // Fall back to term-based endpoint lookups.
          }
        }

        const [inferredGroup, inferredSupplier] = await Promise.all([
          shouldInferGroup
            ? inferGroup(options.baseEndpoint, parsed, {
                preloadedCandidates: preloadedGroups,
                lines: meaningfulLines,
                sourceText: rawText,
                onDebugBest: (best) => {
                  debugLastBestGroup = best;
                },
              })
            : Promise.resolve({ candidate: null, confidence: 0 }),
          shouldInferSupplier
            ? inferSupplier(options.baseEndpoint, parsed, meaningfulLines, {
                preloadedCandidates: preloadedSuppliers,
                sourceText: rawText,
                onDebugBest: (best) => {
                  debugLastBestSupplier = best;
                },
              })
            : Promise.resolve({ candidate: null, confidence: 0 }),
        ]);
        if (currentInfer !== inferSequence) {
          return;
        }

        if (shouldInferGroup && inferredGroup.candidate) {
          const groupAccepted = inferredGroup.confidence >= MIN_INFERENCE_CONFIDENCE;
          const existingHasGroup = !!(existingPart && hasValidRelationId(existingPart.part_part_group_id));
          // A weak re-match must not replace the group already stored on a found part.
          if (!groupSelectionLocked && (groupAccepted || !existingHasGroup)) {
            setConfidenceState(
              popup,
              'group',
              inferredGroup.confidence,
              groupAccepted,
              getDisplayName(inferredGroup.candidate)
            );

            // Always keep the top match visible/selected; icon color still reflects confidence threshold.
            applyGroupSelection(popup, inferredGroup.candidate);
            recomputeSellingPrice(popup);
          }
        } else if (shouldInferGroup && !groupSelectionLocked && !(existingPart && hasValidRelationId(existingPart.part_part_group_id))) {
          applyGroupSelection(popup, null);
        }

        if (shouldInferSupplier && currentSupplierInfer === supplierInferSequence) {
          if (inferredSupplier.candidate) {
            const supplierAccepted = inferredSupplier.confidence >= MIN_INFERENCE_CONFIDENCE;
            if (!supplierSelectionLocked) {
              setConfidenceState(
                popup,
                'supplier',
                inferredSupplier.confidence,
                supplierAccepted,
                getDisplayName(inferredSupplier.candidate)
              );

              // Always keep the top match visible/selected; icon color still reflects confidence threshold.
              applySupplierSelection(popup, inferredSupplier.candidate);
              syncExistingPartToSelectedSupplier();
              refreshSupplierMismatchSuggestion();
            }
          } else if (!supplierSelectionLocked) {
            applySupplierSelection(popup, null);
          }
        }

        const supplierLabel = debugLastBestSupplier && debugLastBestSupplier.candidate
          ? (getDisplayName(debugLastBestSupplier.candidate) || '(sans label)')
          : 'aucun';
        const supplierScore = debugLastBestSupplier && Number.isFinite(debugLastBestSupplier.confidence)
          ? `${Math.round(debugLastBestSupplier.confidence * 100)}%`
          : '0%';
        const groupLabel = debugLastBestGroup && debugLastBestGroup.candidate
          ? (getDisplayName(debugLastBestGroup.candidate) || '(sans label)')
          : 'aucun';
        const groupScore = debugLastBestGroup && Number.isFinite(debugLastBestGroup.confidence)
          ? `${Math.round(debugLastBestGroup.confidence * 100)}%`
          : '0%';

        setDebugMessage(
          popup,
          `Pool fournisseurs: ${preloadedSuppliers.length} | top: ${supplierLabel} (${supplierScore}) || Pool groupes: ${preloadedGroups.length} | top: ${groupLabel} (${groupScore})`
        );
      } catch (error) {
        if (shouldInferGroup && currentInfer === inferSequence) {
          applyGroupSelection(popup, null);
        }
        if (shouldInferSupplier && currentSupplierInfer === supplierInferSequence) {
          applySupplierSelection(popup, null);
        }
      }
    }
  }

  popup.on('shown.bs.modal', function() {
    const select2DropdownParent = resolveDropdownParent();

    preloadOptionsPromise = Promise.all([
      fetchAllSelect2Options(
        options.baseEndpoint,
        'part/select2/part_supplier_id',
        25,
        ['a', 'e', 'i', 'o', 'u', 's', 'r', 't', 'n', 'l', '1']
      )
        .then((items) => {
          preloadedSuppliers = items;
        }),
      fetchAllSelect2Options(
        options.baseEndpoint,
        'part/select2/part_part_group_id',
        25,
        ['a', 'e', 'i', 'o', 'u', 'p', 'f', 'b', 'm', '1']
      )
        .then((items) => {
          preloadedGroups = items;
        }),
    ]);

    preloadOptionsPromise.then(() => {
      setDebugMessage(popup, `Pool fournisseurs: ${preloadedSuppliers.length} | Pool groupes: ${preloadedGroups.length}`);
    }).catch(() => {
      setDebugMessage(popup, 'Debug: echec prechargement fournisseurs/groupes (fallback AJAX par termes actif).');
    });

    popup.find('.rapid-part-supplier').select2({
      language: 'fr',
      allowClear: true,
      placeholder: '',
      dropdownParent: select2DropdownParent,
      ajax: {
        url: `${options.baseEndpoint}part/select2/part_supplier_id`,
        dataType: 'json',
        data: function(params) {
          return {
            search: params.term,
            page: params.page || 1,
          };
        },
        processResults: function(data) {
          return data;
        }
      }
    }).on('select2:select', function(event) {
      supplierSelectionLocked = true;
      applySupplierSelection(popup, event.params.data);
      syncExistingPartToSelectedSupplier();
      refreshSupplierMismatchSuggestion();
      setConfidenceState(popup, 'supplier');
    }).on('select2:open', function() {
      bringOpenSelect2AboveModal(select2DropdownParent);
    }).on('select2:clear', function() {
      supplierSelectionLocked = true;
      applySupplierSelection(popup, null);
      refreshSupplierMismatchSuggestion();
      setConfidenceState(popup, 'supplier');
      refreshExistingDiffState(popup);
    });

    popup.find('.rapid-part-group').select2({
      language: 'fr',
      allowClear: true,
      placeholder: '',
      dropdownParent: select2DropdownParent,
      ajax: {
        url: `${options.baseEndpoint}part/select2/part_part_group_id`,
        dataType: 'json',
        data: function(params) {
          return {
            search: params.term,
            page: params.page || 1,
          };
        },
        processResults: function(data) {
          return data;
        }
      }
    }).on('select2:select', function(event) {
      groupSelectionLocked = true;
      applyGroupSelection(popup, event.params.data);
      setConfidenceState(popup, 'group');
      recomputeSellingPrice(popup);
    }).on('select2:open', function() {
      bringOpenSelect2AboveModal(select2DropdownParent);
    }).on('select2:clear', function() {
      groupSelectionLocked = true;
      applyGroupSelection(popup, null);
      setConfidenceState(popup, 'group');
      refreshExistingDiffState(popup);
    });

    bindFieldEvents(popup);

    popup.on('click keydown', '.rapid-part-found-item', function(event) {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      const part = foundParts[parseInt($(this).attr('data-index'), 10)];
      if (part) {
        selectFoundPart(part);
      }
    });

    popup.on('click keydown', '.rapid-part-version-value', function(event) {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      popup.find($(this).attr('data-field')).val($(this).attr('data-value'));
      updateSaveButtonState(popup);
      refreshExistingDiffState(popup);
    });

    popup.find('.rapid-part-source')
      .on('input', analyzeSourceText)
      .on('dragover', function(event) {
        event.preventDefault();
      })
      .on('drop', function(event) {
        event.preventDefault();
        const transfer = event.originalEvent && event.originalEvent.dataTransfer;
        if (!transfer) {
          return;
        }

        const text = transfer.getData('text/plain');
        if (text) {
          $(this).val(text);
          analyzeSourceText();
        }
      });

    popup.find('.rapid-part-confirm').on('click', function() {
      recomputeSellingPrice(popup);
      const payload = buildPayload(popup);

      if (!payload.part_number || !payload.part_name) {
        toastr.error('Le numéro de pièce et le nom sont requis.');
        return;
      }

      const saveMode = getSelectedSaveMode(popup);
      const isOtherSupplierMatch = !!popup.data('existing-part-other-supplier');
      const useExistingPart = !!(existingPart && saveMode === 'existing');
      const updateExistingPart = !!(existingPart && !isOtherSupplierMatch && useExistingPart);
      const useOtherSupplierPart = !!(existingPart && isOtherSupplierMatch && useExistingPart);

      if (useOtherSupplierPart) {
        options.onSelected(normalizePartForSelection(existingPart));
        popup.modal('hide');
        return;
      }

      const requestData = $.param(payload);
      const saveRequest = updateExistingPart
        ? options.updateItem(existingPart.part_id || existingPart.id, requestData)
        : options.createItem(requestData);

      saveRequest
        .then((result) => {
          options.onSelected(result);
          popup.modal('hide');
        })
        .catch(() => {
          // The shared request helpers already surface the server error.
        });
    });

    // Deferred so a parent modal's focus enforcement cannot take focus back.
    setTimeout(() => popup.find('.rapid-part-source').trigger('focus'), 100);
    setTimeout(() => {
      const source = popup.find('.rapid-part-source');
      if (!source.is(':focus')) {
        source.trigger('focus');
      }
    }, 500);
    updateSaveButtonState(popup);
  });

  return popup;
}

module.exports = {
  openRapidPartModal,
  getSellingFormula,
  parseRawPartText,
  inferGroup,
  inferSupplier,
  normalizeGroupMatchText,
  shouldInferGroupForPart,
};
